<script setup>
import { useStore } from 'vuex';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import AccordionButton from '@/components/buttons/AccordionButton.vue';
import { ref, computed, onUnmounted, onMounted, useCssModule } from 'vue';
import { useRouter } from 'vue-router';
import MenuLinks from '@/components/MenuLinks.vue';
import { useWayfinding } from '@/composables/wayfinding';

const store = useStore();
const router = useRouter();
const $style = useCssModule();
const displayAccountDropdown = ref(false);
const emits = defineEmits(['click']);

const { accountDisplayLinks } = useWayfinding();

const eventCommonProps = computed(() => ({
  targetArea: router.currentRoute.value.name,
  productSegment: store.getters.productSegmentLabel,
  userId: store.getters.userId,
  businessId: store.getters.businessId,
}));

function toggleAccountMenu() {
  const event = displayAccountDropdown.value
    ? SEGMENT_EVENTS.fe_my_account_collapse_clicked
    : SEGMENT_EVENTS.fe_my_account_expand_clicked;
  analytics.track(event, {
    ...eventCommonProps.value,
  });
  displayAccountDropdown.value = !displayAccountDropdown.value;
}

function handleLinkClick(args) {
  displayAccountDropdown.value = false;
  emits('click', args);
}

function closeAccountMenu() {
  if (displayAccountDropdown.value) {
    displayAccountDropdown.value = false;
  }
}

const myAccountDropdownClasses = computed(() => ({
  [$style['account-dropdown']]: true,
  [$style.selected]: accountDisplayLinks.value.children
    ?.map((link) => link.route)
    .includes(router.currentRoute.value.name),
}));

onUnmounted(() => {
  window.removeEventListener('resize', closeAccountMenu);
});

onMounted(() => {
  window.addEventListener('resize', closeAccountMenu);
});
</script>

<template>
  <div
    v-click-outside="closeAccountMenu"
    :class="$style['my-account-dropdown']"
  >
    <button
      :class="myAccountDropdownClasses"
      data-ref="my-account-button"
      data-cy="my-account-button"
      aria-label="my account dropdown"
      @click="toggleAccountMenu"
    >
      {{ $t('data.wayfinding.wayfindingMenuTemplate.myAccount') }}
      <span :class="$style['accordion-container']">
        <AccordionButton
          :is-inverted="displayAccountDropdown"
          :class="$style['accordion-button']"
        />
      </span>
    </button>
    <MenuLinks
      v-if="displayAccountDropdown"
      :links="accountDisplayLinks"
      list-children-only
      :class="$style['account-children']"
      @linkClicked="handleLinkClick"
    />
  </div>
</template>

<style lang="less" module>
.my-account-dropdown {
  display: block;
  @media only screen and (max-width: @wayfinding-tablet-width) {
    display: none;
  }
}
.account-dropdown {
  align-items: center;
  background: none;
  border: none;
  color: @cc-light-brown;
  cursor: pointer;
  display: flex;
  font-family: @regular-font;
  font-size: @font-size-14;
  font-weight: 400;
  height: 60px;
  margin-right: 40px;
  padding: unset;
  &.selected {
    font-weight: 700;
    color: @color-black;
  }
  &:focus {
    outline: none;
  }
  .accordion-container {
    padding-left: 5px;
    .accordion-button {
      height: 25px;
      width: 25px;
      padding: 5px;
    }
  }
}
.account-children {
  background: @color-white;
  border-radius: 4px;
  border: 1px solid @color-grey-200;
  padding: 20px 10px;
  position: absolute;
  right: 3.5rem;
  top: 4rem;
  z-index: 2;
}
@media only screen and (max-width: @wayfinding-tablet-width) {
  .account-dropdown,
  .account-children {
    display: none;
  }
}
</style>
